import { useMemo, useState } from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import {
  defaultValuesFor,
  type CalculatorDefinition,
  type CalculatorOutput,
  type CalculatorValues,
} from '@/content/financial/calculators';
import {
  formatDollars,
  formatMonths,
  formatMultiple,
  formatNumber,
  formatPercent,
} from '@/content/financial/format';
import FieldInput from './FieldInput';

/**
 * Renders any calculator: inputs on the left, live results on the right.
 *
 * Recomputes on every keystroke — all four definitions are cheap pure
 * functions, so there is nothing to debounce.
 *
 * Prerender note: this mounts with `defaultValuesFor(def)`, so the static HTML
 * carries a fully worked example rather than an empty form. That is what makes
 * a calculator route worth indexing at all.
 */
export default function CalculatorShell({ def }: { def: CalculatorDefinition }) {
  const [values, setValues] = useState<CalculatorValues>(() => defaultValuesFor(def));

  const result = useMemo(() => {
    try {
      return def.compute(values);
    } catch {
      // A calculator must never take the page down over a bad intermediate
      // value; show nothing rather than crash the route.
      return { outputs: [], warnings: ['Could not compute with these inputs.'] };
    }
  }, [def, values]);

  const visibleFields = def.fields.filter((f) => !f.visibleWhen || f.visibleWhen(values));
  const groups = groupFields(visibleFields);

  const primary = result.outputs.filter((o) => o.primary);
  const secondary = result.outputs.filter((o) => !o.primary);

  const isDirty = def.fields.some((f) => values[f.id] !== f.defaultValue);

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,7fr)_minmax(0,8fr)] lg:items-start">
      {/* ── Inputs ── */}
      <section
        aria-label="Calculator inputs"
        className="rounded-xl border border-ink-200 bg-white p-5 shadow-sm dark:border-surface-dark-border dark:bg-surface-dark-card sm:p-6"
      >
        <div className="mb-5 flex items-center justify-between gap-3">
          <h2 className="text-lg font-semibold text-ink-900 dark:text-ink-50">Your numbers</h2>
          {isDirty && (
            <button
              type="button"
              onClick={() => setValues(defaultValuesFor(def))}
              className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium text-ink-500 transition-colors hover:bg-ink-100 hover:text-ink-900 dark:text-ink-400 dark:hover:bg-white/10 dark:hover:text-ink-50"
            >
              <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
              Reset
            </button>
          )}
        </div>

        <div className="space-y-6">
          {groups.map(([group, fields]) => (
            <fieldset key={group} className="space-y-4">
              <legend className="mb-3 w-full border-b border-ink-200 pb-2 text-[11px] font-bold uppercase tracking-wide text-ink-500 dark:border-surface-dark-border dark:text-ink-400">
                {group}
              </legend>
              {fields.map((field) => (
                <FieldInput
                  key={field.id}
                  field={field}
                  value={values[field.id] ?? field.defaultValue}
                  onChange={(next) => setValues((prev) => ({ ...prev, [field.id]: next }))}
                />
              ))}
            </fieldset>
          ))}
        </div>
      </section>

      {/* ── Results ── */}
      <div className="space-y-4 lg:sticky lg:top-24">
        <section
          aria-label="Results"
          className="overflow-hidden rounded-xl border border-ink-200 bg-white shadow-sm dark:border-surface-dark-border dark:bg-surface-dark-card"
        >
          <div className="border-b border-ink-200 bg-ink-50 px-5 py-4 dark:border-surface-dark-border dark:bg-white/[0.03] sm:px-6">
            <h2 className="text-lg font-semibold text-ink-900 dark:text-ink-50">Results</h2>
          </div>

          {primary.length > 0 && (
            <div
              className={`grid divide-y divide-ink-200 border-b border-ink-200 dark:divide-surface-dark-border dark:border-surface-dark-border ${
                primary.length >= 3
                  ? 'sm:grid-cols-3 sm:divide-x sm:divide-y-0'
                  : 'sm:grid-cols-2 sm:divide-x sm:divide-y-0'
              }`}
            >
              {primary.map((output) => (
                <OutputCell key={output.id} output={output} />
              ))}
            </div>
          )}

          <dl className="divide-y divide-ink-200 dark:divide-surface-dark-border">
            {secondary.map((output) => (
              <OutputRow key={output.id} output={output} />
            ))}
          </dl>
        </section>

        {result.warnings && result.warnings.length > 0 && (
          <div className="space-y-2.5 rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/40 dark:bg-amber-900/10">
            {result.warnings.map((warning, i) => (
              <p
                key={i}
                className="flex gap-2.5 text-sm leading-relaxed text-amber-900 dark:text-amber-200"
              >
                <AlertTriangle
                  className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-500"
                  aria-hidden="true"
                />
                <span>{warning}</span>
              </p>
            ))}
          </div>
        )}

        {def.disclaimer && (
          <p className="px-1 text-xs leading-relaxed text-ink-500 dark:text-ink-400">
            {def.disclaimer}
          </p>
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function groupFields(fields: CalculatorDefinition['fields']) {
  const map = new Map<string, CalculatorDefinition['fields']>();
  for (const field of fields) {
    const key = field.group ?? 'Inputs';
    const existing = map.get(key);
    if (existing) existing.push(field);
    else map.set(key, [field]);
  }
  return [...map.entries()];
}

function formatOutput(output: CalculatorOutput): string {
  switch (output.format) {
    case 'currency':
      return formatDollars(output.value, 0);
    case 'currency-exact':
      return formatDollars(output.value, 2);
    case 'percent':
      return formatPercent(output.value, 1);
    case 'multiple':
      return formatMultiple(output.value, 2);
    case 'months':
      return formatMonths(output.value);
    case 'number-1dp':
      return formatNumber(output.value, 1);
    case 'number':
    default:
      return formatNumber(output.value, 0);
  }
}

/**
 * Tone → colour.
 *
 * `risk` uses semantic red (#DC2626 / red-600), not Jeok. DESIGN.md keeps the
 * two deliberately distinct so a bad number never reads as brand chrome.
 */
const TONE_CLASS = {
  neutral: 'text-ink-900 dark:text-ink-50',
  currency: 'text-green-600 dark:text-green-500',
  cost: 'text-amber-600 dark:text-amber-500',
  risk: 'text-red-600 dark:text-red-400',
} as const;

function OutputCell({ output }: { output: CalculatorOutput }) {
  return (
    <div className="px-5 py-5 sm:px-6">
      <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-ink-500 dark:text-ink-400">
        {output.label}
      </p>
      <p
        className={`font-mono text-3xl font-semibold tracking-tight tabular-nums ${
          TONE_CLASS[output.tone ?? 'neutral']
        }`}
      >
        {formatOutput(output)}
      </p>
      {output.note && (
        <p className="mt-2 text-xs leading-relaxed text-ink-500 dark:text-ink-400">
          {output.note}
        </p>
      )}
    </div>
  );
}

function OutputRow({ output }: { output: CalculatorOutput }) {
  return (
    <div className="flex items-baseline justify-between gap-4 px-5 py-3.5 sm:px-6">
      <div className="min-w-0">
        <dt className="text-sm text-ink-700 dark:text-ink-200">{output.label}</dt>
        {output.note && (
          <dd className="mt-0.5 text-xs leading-relaxed text-ink-500 dark:text-ink-400">
            {output.note}
          </dd>
        )}
      </div>
      <dd
        className={`shrink-0 font-mono text-sm font-semibold tabular-nums ${
          TONE_CLASS[output.tone ?? 'neutral']
        }`}
      >
        {formatOutput(output)}
      </dd>
    </div>
  );
}
